import React, { useEffect, useState } from 'react';
import { Avatar, Col, Row, Skeleton, Tag, Typography } from 'antd';
import { Delivery, DeliveryAction, DeliveryStatus } from '../../api/models/deliveries';
import { canBeCanceled, getDirectionType } from '../../utils/deliveries';
import {
  DeleteOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FlagOutlined,
  PhoneOutlined,
  ReadOutlined,
  RollbackOutlined,
  ShopOutlined,
  UserOutlined
} from '@ant-design/icons';
import SkeletonAvatar from 'antd/lib/skeleton/Avatar';
import i18next from '../../i18next';
import Button from '../Button/Button';
import SmallBuildingIcon from '../../assets/img/smallBuilding.svg?react';
import RiderBlackIcon from '../../assets/img/riderBlack.svg?react';
import styles from './ActiveDeliveryExpanded.module.less';
import Map from '../Map';
import { useDispatch, useSelector } from 'react-redux';
import { deliveriesSelector } from '../../redux/selectors/deliveries';
import { AppDispatch } from '../../types/store';
import { DrivingDistanceRoutesTransformed } from '../../api/models/locations';
import { MapUseCase } from '../Map/Map';
import DeliveryAttribute from '../DeliveryAttribute';
import classnames from 'classnames';
import StepsStatus from '../StepsStatus';
import DoorstepIcon from '../../assets/img/doorstep.svg?react';
import { colorBgBase } from '../../styles/theme';

const renderDeliveryActionTag = (deliveryAction: DeliveryAction) => {
  const tagStyle = { alignItems: 'center', columnGap: '5px' };

  switch (deliveryAction) {
    case DeliveryAction.CALL_MERCHANT:
      return (
        <Tag data-testid={`tag_${DeliveryAction.CALL_MERCHANT}`} style={{ background: colorBgBase }}>
          <Row style={tagStyle}>
            <PhoneOutlined />
            {i18next.t('deliveries.doorstep.CALL_MERCHANT.shortOptionName')}
          </Row>
        </Tag>
      );
    case DeliveryAction.LEAVE_AT_DOOR:
      return (
        <Tag data-testid={`tag_${DeliveryAction.LEAVE_AT_DOOR}`} style={{ background: colorBgBase }}>
          <Row style={tagStyle}>
            <DoorstepIcon /> {i18next.t('deliveries.doorstep.LEAVE_AT_DOOR.shortOptionName')}
          </Row>
        </Tag>
      );
    case DeliveryAction.RETURN:
      return (
        <Tag data-testid={`tag_${DeliveryAction.RETURN}`} style={{ background: colorBgBase }}>
          <Row style={tagStyle}>
            <RollbackOutlined />
            {i18next.t('deliveries.doorstep.RETURN.shortOptionName')}
          </Row>
        </Tag>
      );
    default:
      return '';
  }
};

const ExpandedTableRow = ({
  delivery: {
    cashOnDelivery,
    vehicle,
    pickupLocation,
    dropOffLocation,
    driverDetails,
    driverNotes,
    passenger,
    status,
    id,
    deliveryAction
  },
  openCancellationModal,
  isCancellationInProgress = false
}: {
  delivery: Delivery;
  openCancellationModal: () => void;
  isCancellationInProgress: boolean;
}) => {
  const [drivingDirections, setDrivingDirections] = useState<DrivingDistanceRoutesTransformed | null>(null);

  const isCancellable = canBeCanceled(status);
  const { deliveriesTrackingDataById } = useSelector(deliveriesSelector);
  const {
    deliveries: { getDistance }
  } = useDispatch<AppDispatch>();

  useEffect(() => {
    if (status !== DeliveryStatus.Pending && pickupLocation && dropOffLocation) {
      const setDistanceDirections = async () => {
        const { directions } = await getDistance({
          pickupLocation: pickupLocation.location,
          //@ts-ignore
          dropOffLocation: dropOffLocation.location
        });
        setDrivingDirections(directions);
      };
      setDistanceDirections().catch(() => {});
    }
  }, []);

  const trackingData = deliveriesTrackingDataById[id];

  return (
    <div className={styles.expandedCardWrapper}>
      <Row data-testid="active-expanded-content-part" gutter={[20, 20]}>
        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <Row>
            <Col span={24}>
              <Row className={styles.detailsRow}>
                <Col className={classnames([styles.cardLeftCol, styles.stepsContainerWrapper])}>
                  <StepsStatus status={status} />
                </Col>
                <Col>
                  <Row>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={pickupLocation?.nickname || pickupLocation?.name}
                        attributeIcon={<ShopOutlined />}
                        isTitle
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={pickupLocation?.nickname || pickupLocation?.name}
                        attributeIcon={<EnvironmentOutlined />}
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={pickupLocation?.number || '-'}
                        attributeIcon={<PhoneOutlined />}
                        textType={'secondary'}
                      />
                    </Col>
                  </Row>
                  <Row className={styles.nextDetailsBlock}>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={passenger ? `${passenger.firstName} ${passenger.lastName}` : '-'}
                        attributeIcon={<FlagOutlined />}
                        isTitle
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className={styles.detailsRow}>
                <Col className={styles.cardLeftCol} />
                <Col>
                  <Row>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={
                          dropOffLocation?.name || dropOffLocation?.area || dropOffLocation?.street || '-'
                        }
                        attributeIcon={<EnvironmentOutlined />}
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={dropOffLocation?.building || '-'}
                        attributeIcon={<SmallBuildingIcon />}
                        textType={'secondary'}
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={driverNotes || '-'}
                        attributeIcon={<ReadOutlined />}
                        textType={'secondary'}
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={passenger?.phoneNumber || '-'}
                        attributeIcon={<PhoneOutlined />}
                        textType={'secondary'}
                      />
                    </Col>
                    {deliveryAction && (
                      <Col style={{ display: 'flex', columnGap: '5px', flexWrap: 'wrap', rowGap: '5px' }} span={24}>
                        <Typography.Text type="secondary">
                          {i18next.t('deliveries.doorstep.ifCustomerNotAvailable')}
                        </Typography.Text>
                        {renderDeliveryActionTag(deliveryAction)}
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
              <Row className={classnames([styles.nextDetailsBlock, styles.detailsRow])}>
                <Col className={styles.cardLeftCol}>
                  {driverDetails ? (
                    <Avatar size={40} icon={<UserOutlined />} src={driverDetails?.driverPicture} />
                  ) : (
                    <SkeletonAvatar active size={40} />
                  )}
                </Col>

                {driverDetails ? (
                  <Col style={{ flex: '1 1 auto' }}>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={`${i18next.t('activeDeliveriesPage.yourDriver')}: ${driverDetails?.driverName}`}
                        isTitle
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={
                          <Col style={{ display: 'flex', columnGap: '5px', flexWrap: 'wrap', rowGap: '5px' }}>
                            <Typography.Text>{`${vehicle?.vehicleModel}`}</Typography.Text>
                            {vehicle && <Tag style={{ background: colorBgBase }}>{vehicle?.plateNumber}</Tag>}
                          </Col>
                        }
                        attributeIcon={<RiderBlackIcon />}
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={`${i18next.t('activeDeliveriesPage.cashToBeCollected')}: ${
                          cashOnDelivery || '-'
                        }`}
                        attributeIcon={<DollarOutlined />}
                        textType={'secondary'}
                      />
                    </Col>
                    <Col span={24}>
                      <DeliveryAttribute
                        attributeValue={driverDetails?.driverNumber || '-'}
                        attributeIcon={<PhoneOutlined />}
                        textType={'secondary'}
                      />
                    </Col>
                  </Col>
                ) : (
                  <Col data-testid="loaderDriver" style={{ flex: '0.8 1 auto' }}>
                    <Skeleton active title={false} paragraph={{ rows: 4 }} loading />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>

        <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
          <Map
            mapUseCase={MapUseCase.RequestDelivery}
            pickup={pickupLocation}
            dropOff={status !== DeliveryStatus.Pending ? dropOffLocation : {}}
            driver={trackingData?.driverLocationDetails && { location: trackingData.driverLocationDetails }}
            directionType={getDirectionType(status)}
            directions={drivingDirections}
            useExternalDirections
            bookingId={id}
            driverEta={trackingData?.eta}
            containerClassName={styles.mapContainer}
          />
        </Col>
      </Row>
      <Row data-testid="active-expanded-button-part" className={styles.cancelButtonRow}>
        <Col xs={24} sm={12} md={8} xl={6}>
          <Button
            disabled={!isCancellable || isCancellationInProgress}
            loading={isCancellationInProgress}
            onClick={openCancellationModal}
            size={'large'}
            icon={<DeleteOutlined />}
            danger
          >
            {i18next.t('deliveries.cancelBtn.text')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default ExpandedTableRow;
